import { css } from '@emotion/react';
import { FC, useMemo } from 'react';
import { Box, Flex, Select, Text } from '@chakra-ui/react';
import { FinancialStatement } from '../../../apis/backend';
import { colors, fontStyles, spacing } from '../../../styles';
import formatDisplay, { FormatterStyle } from '../../../utils/formatDisplay';

interface Props {
  data: FinancialStatement[] | undefined;
  comparisonData?: FinancialStatement[] | null;
  baseSymbol?: string;
  comparisonSymbol?: string;
  metric: string;
  periodType: 'quarterly' | 'annual' | 'ttm';
  onPeriodChange: (type: 'quarterly' | 'annual' | 'ttm') => void;
  currency?: string;
}

const METRIC_OPTIONS = [
  { value: 'totalRevenue', label: 'Revenue' },
  { value: 'netIncome', label: 'Net Income' },
  { value: 'grossProfit', label: 'Gross Profit' },
  { value: 'ebitda', label: 'EBITDA' },
  { value: 'operatingCashflow', label: 'Operating CF' },
  { value: 'operatingIncome', label: 'Operating Income' },
  { value: 'freeCashFlow', label: 'FCF' },
];

const getMetricValue = (statement: FinancialStatement, metric: string): number | null => {
  // FCF is computed: operatingCashflow - |capitalExpenditures|
  if (metric === 'freeCashFlow') {
    if (statement.operatingCashflow === null || statement.capitalExpenditures === null) {
      return null;
    }
    return statement.operatingCashflow - Math.abs(statement.capitalExpenditures);
  }
  return statement[metric as keyof FinancialStatement] as number | null;
};

const formatQuarterLabel = (dateStr: string, isAnnual: boolean): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear().toString().slice(-2);
  if (isAnnual) {
    return `FY'${year}`;
  }
  const month = date.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter}'${year}`;
};

// Sum 4 quarters starting from index for rolling TTM calculation
const sumTTMMetric = (statements: FinancialStatement[], startIndex: number, metric: string): number | null => {
  const quarters = statements.slice(startIndex, startIndex + 4);
  if (quarters.length < 4) return null;

  const values = quarters.map((s) => getMetricValue(s, metric));
  if (values.every((v) => v === null)) return null;
  return values.reduce((sum, v) => (sum ?? 0) + (v ?? 0), 0);
};

const FinancialsChart: FC<Props> = ({
  data,
  comparisonData,
  baseSymbol,
  comparisonSymbol,
  metric,
  periodType,
  onPeriodChange,
  currency,
}) => {
  const isComparing = !!comparisonData && !!comparisonSymbol;

  // Build a map of comparison data by fiscal period label for matching
  const comparisonMap = useMemo(() => {
    if (!comparisonData) return new Map<string, FinancialStatement>();
    const map = new Map<string, FinancialStatement>();
    comparisonData.forEach((statement) => {
      const label = formatQuarterLabel(statement.fiscalDate, statement.isAnnual);
      map.set(label, statement);
    });
    return map;
  }, [comparisonData]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // TTM mode: show rolling TTM values from quarterly data
    if (periodType === 'ttm') {
      // Need at least 4 quarters for first TTM
      if (data.length < 4) return [];

      // Calculate how many rolling TTM periods we can show
      // With 8 quarters, we can show 5 TTM periods (index 0-4, each using 4 quarters)
      const maxTTMPeriods = data.length - 3;
      const numPeriods = isComparing ? Math.min(maxTTMPeriods, 5) : Math.min(maxTTMPeriods, 6);

      // Generate TTM data points (oldest to newest for display)
      const ttmData = [];
      for (let i = numPeriods - 1; i >= 0; i--) {
        const value = sumTTMMetric(data, i, metric);
        const latestQuarter = data[i];
        const label = `TTM ${formatQuarterLabel(latestQuarter.fiscalDate, false)}`;

        // Get comparison TTM value for same period
        let comparisonValue: number | null = null;
        if (comparisonData && comparisonData.length >= i + 4) {
          comparisonValue = sumTTMMetric(comparisonData, i, metric);
        }

        // Calculate YoY change (compare to TTM from 4 quarters ago)
        let yoyChange: number | null = null;
        if (i + 4 < data.length) {
          const previousTTMValue = sumTTMMetric(data, i + 4, metric);
          if (value !== null && previousTTMValue !== null && previousTTMValue !== 0) {
            yoyChange = (value - previousTTMValue) / previousTTMValue;
          }
        }

        ttmData.push({ label, value, comparisonValue, yoyChange });
      }

      return ttmData;
    }

    // Quarterly/Annual mode
    const numPeriods = isComparing ? 6 : 8;
    const periods = [...data].slice(0, numPeriods).reverse();

    return periods.map((statement, index) => {
      const value = getMetricValue(statement, metric);
      const label = formatQuarterLabel(statement.fiscalDate, statement.isAnnual);

      // Get comparison value for same period
      const comparisonStatement = comparisonMap.get(label);
      const comparisonValue = comparisonStatement ? getMetricValue(comparisonStatement, metric) : null;

      let yoyChange: number | null = null;

      // Calculate YoY change (compare to same period last year)
      // For negative base values (losses), we still use the actual value to get correct sign:
      // -100 to -50 (smaller loss) = (-50 - -100) / -100 = 50/-100 = -50% (improvement shows as reduction)
      // -100 to -150 (bigger loss) = (-150 - -100) / -100 = -50/-100 = +50% (deterioration shows as increase)
      if (periodType === 'quarterly' && index >= 4) {
        const previousYearValue = getMetricValue(periods[index - 4], metric);
        if (value !== null && previousYearValue !== null && previousYearValue !== 0) {
          yoyChange = (value - previousYearValue) / previousYearValue;
        }
      } else if (periodType === 'annual' && index >= 1) {
        const previousYearValue = getMetricValue(periods[index - 1], metric);
        if (value !== null && previousYearValue !== null && previousYearValue !== 0) {
          yoyChange = (value - previousYearValue) / previousYearValue;
        }
      }

      return {
        label,
        value,
        comparisonValue,
        yoyChange,
      };
    });
  }, [data, comparisonData, comparisonMap, metric, periodType, isComparing]);

  const maxValue = useMemo(() => {
    const values = chartData.flatMap((d) => [d.value, d.comparisonValue]).filter((v): v is number => v !== null);
    return values.length > 0 ? Math.max(...values.map(Math.abs)) : 0;
  }, [chartData]);

  const metricLabel = METRIC_OPTIONS.find((o) => o.value === metric)?.label || metric;

  if (!data || data.length === 0) {
    return (
      <Box css={styles.container}>
        <Text css={fontStyles.cat} color={colors.tungsten}>
          No financial data available
        </Text>
      </Box>
    );
  }

  return (
    <Box css={styles.container}>
      <Flex css={styles.header} justify="space-between" align="center">
        <Text css={fontStyles.fatCat}>{metricLabel}</Text>
        <Select
          size="sm"
          value={periodType}
          onChange={(e) => onPeriodChange(e.target.value as 'quarterly' | 'annual' | 'ttm')}
          width="120px"
          borderColor={colors.cloud}
          _dark={{ borderColor: colors.tungsten }}
        >
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
          <option value="ttm">TTM</option>
        </Select>
      </Flex>

      {/* Legend when comparing */}
      {isComparing && (
        <Flex css={styles.legend} gap={spacing.deci}>
          <Flex align="center" gap={spacing.milli}>
            <Box css={styles.legendDot} />
            <Text css={fontStyles.flea}>{baseSymbol}</Text>
          </Flex>
          <Flex align="center" gap={spacing.milli}>
            <Box css={styles.legendDotComparison} />
            <Text css={fontStyles.flea}>{comparisonSymbol}</Text>
          </Flex>
        </Flex>
      )}

      {/* Column-based chart layout */}
      <Flex css={styles.chartContainer}>
        {chartData.map((item, index) => {
          const barHeight = item.value !== null && maxValue > 0 ? (Math.abs(item.value) / maxValue) * 100 : 0;
          const comparisonBarHeight =
            item.comparisonValue !== null && maxValue > 0 ? (Math.abs(item.comparisonValue) / maxValue) * 100 : 0;
          const isNegative = item.value !== null && item.value < 0;
          const isComparisonNegative = item.comparisonValue !== null && item.comparisonValue < 0;

          return (
            <Flex key={index} css={styles.column} direction="column" align="center">
              {/* Bar area */}
              <Flex css={styles.barArea} align="flex-end" justify="center">
                <Flex css={styles.barGroup} align="flex-end" justify="center">
                  <Box
                    css={[styles.barWrapper, isComparing && styles.barWrapperCompareMode]}
                    height={`${Math.max(barHeight, 4)}%`}
                  >
                    <Text css={styles.valueLabel}>
                      {item.value !== null ? formatDisplay(FormatterStyle.moneyCompact, item.value, currency) : '—'}
                    </Text>
                    <Box css={[styles.bar, isComparing && styles.barCompareMode, isNegative && styles.negativeBar]} />
                  </Box>
                  {isComparing && (
                    <Box css={styles.barWrapper} height={`${Math.max(comparisonBarHeight, 4)}%`}>
                      <Text css={styles.valueLabel}>
                        {item.comparisonValue !== null
                          ? formatDisplay(FormatterStyle.moneyCompact, item.comparisonValue, currency)
                          : '—'}
                      </Text>
                      <Box css={[styles.comparisonBar, isComparisonNegative && styles.negativeBar]} />
                    </Box>
                  )}
                </Flex>
              </Flex>

              {/* Quarter label */}
              <Text css={styles.quarterLabel}>{item.label}</Text>

              {/* YoY label - only show when not comparing */}
              {!isComparing && (
                <Text
                  css={styles.yoyLabel}
                  color={
                    item.yoyChange !== null
                      ? Math.round(item.yoyChange * 100) === 0
                        ? colors.tungsten
                        : item.yoyChange > 0
                          ? colors.leaf
                          : colors.berry
                      : 'transparent'
                  }
                >
                  {item.yoyChange !== null
                    ? `${item.yoyChange >= 0 ? '+' : ''}${(item.yoyChange * 100).toFixed(0)}%`
                    : ' '}
                </Text>
              )}
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

const styles = {
  container: css`
    margin-bottom: ${spacing.deci};
  `,
  header: css`
    margin-bottom: ${spacing.centi};
  `,
  legend: css`
    margin-bottom: ${spacing.centi};
  `,
  legendDot: css`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: ${colors.petrolBlue};
    html[data-theme='dark'] & {
      background-color: ${colors.skyBlue};
    }
  `,
  legendDotComparison: css`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: ${colors.tungsten};
    html[data-theme='dark'] & {
      background-color: ${colors.cloud};
    }
  `,
  chartContainer: css`
    justify-content: space-between;
  `,
  column: css`
    flex: 1;
  `,
  valueLabel: css`
    ${fontStyles.flea};
    color: ${colors.tungsten};
    text-align: center;
    margin-bottom: 2px;
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  barArea: css`
    width: 100%;
    height: 120px;
    border-bottom: 1px solid ${colors.cloud};
    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};
    }
  `,
  barGroup: css`
    width: 100%;
    height: 100%;
    gap: 2px;
  `,
  barWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 70%;
    max-width: 40px;
  `,
  barWrapperCompareMode: css`
    width: 45%;
    max-width: 28px;
  `,
  bar: css`
    width: 100%;
    flex: 1;
    background-color: ${colors.petrolBlue};
    border-radius: 2px 2px 0 0;
    min-height: 4px;
    html[data-theme='dark'] & {
      background-color: ${colors.skyBlue};
    }
  `,
  barCompareMode: css`
    width: 45%;
    max-width: 24px;
  `,
  comparisonBar: css`
    width: 100%;
    flex: 1;
    background-color: ${colors.tungsten};
    border-radius: 2px 2px 0 0;
    min-height: 4px;
    html[data-theme='dark'] & {
      background-color: ${colors.cloud};
    }
  `,
  negativeBar: css`
    background-color: ${colors.berry};
  `,
  quarterLabel: css`
    ${fontStyles.flea};
    color: ${colors.tungsten};
    text-align: center;
    margin-top: ${spacing.milli};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  yoyLabel: css`
    ${fontStyles.flea};
    text-align: center;
    min-height: 14px;
  `,
};

export default FinancialsChart;
